import { Component, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { EmployeeService } from 'src/app/services/employee.service';
import { IncidentService } from 'src/app/services/incident.service';
import { UtilsService } from 'src/app/services/utils.service';
import { PermissionService } from 'src/app/services/permission.service';
import { TerminationEvent, TerminationModalComponent } from '../termination-modal/termination-modal.component';

interface IncidentTypeMeta {
    value: string;
    label: string;
    color: string;
    icon: string;
    iconBg: string;
}

interface CalendarDay {
    date: Date;
    iso: string;
    day: number;
    dots: string[];
}

const INCIDENT_TYPES: IncidentTypeMeta[] = [
    { value: 'disability', label: 'Incapacidad', color: '#8b5cf6', icon: 'inc-briefcase-purple.svg', iconBg: '#f5f3ff' },
    { value: 'vacation', label: 'Vacaciones', color: '#10b981', icon: 'inc-sun.svg', iconBg: '#e8fcf7' },
    { value: 'leave', label: 'Permiso', color: '#f59e0b', icon: 'inc-file-text-amber.svg', iconBg: '#fdf4e8' },
    { value: 'unexcused_absence', label: 'Falta injustificada', color: '#ef4444', icon: 'inc-user-minus.svg', iconBg: '#fee2e2' },
];

const OTHER_TYPE_META: IncidentTypeMeta = { value: 'other', label: 'Otro', color: '#6b7280', icon: '', iconBg: '' };

const INCAPACITY_TYPES = ['Incapacidad', 'Ausentismo', 'Incapacidad temporal', 'Incapacidad permanente', 'Incapacidad total'];

const MONTH_NAMES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const WEEKDAY_NAMES_LONG = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

@Component({
    selector: 'app-incidents',
    templateUrl: './incidents.component.html',
    styleUrls: ['./incidents.component.css']
})
export class IncidentsComponent implements OnInit {

    @ViewChild('terminationModal') terminationModal!: TerminationModalComponent;

    employeeId!: number;
    employee: any = null;

    readonly typeButtons = INCIDENT_TYPES;
    readonly incapacityTypes = INCAPACITY_TYPES;
    readonly weekdayLabels = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
    readonly legendItems = [
        { label: 'Falta', color: '#ef4444' },
        { label: 'Incapacidad', color: '#8b5cf6' },
        { label: 'Permiso', color: '#f59e0b' },
        { label: 'Vacaciones', color: '#10b981' },
    ];

    rows: any[] = [];
    loading = false;
    loadError = false;
    showAllHistory = false;

    form!: FormGroup;
    submitted = false;
    selectedIncident: any = null;

    viewYear = new Date().getFullYear();
    viewMonth = new Date().getMonth();
    selectedDate: Date | null = null;
    calendarWeeks: (CalendarDay | null)[][] = [];

    // ---- Selección de rango en el calendario (Fecha de inicio/Fecha de fin) ----
    rangeStart: Date | null = null;
    rangeEnd: Date | null = null;
    private rangeSelectionInProgress = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private fb: FormBuilder,
        private employeeService: EmployeeService,
        private incidentService: IncidentService,
        private utils: UtilsService,
        private spinner: NgxSpinnerService,
        private permissionService: PermissionService
    ) { }

    canDo(action: string): boolean {
        return this.permissionService.canDo('Listado de colaboradores', action);
    }

    ngOnInit(): void {
        this.buildForm();
        this.buildCalendar();

        this.route.paramMap.subscribe((params) => {
            const idParam = params.get('id');
            this.employeeId = idParam ? parseInt(idParam, 10) : 0;
            if (!this.employeeId) return;
            this.loadEmployee();
            this.fetchIncidents();
        });
    }

    private buildForm(): void {
        this.form = this.fb.group({
            incident_type: ['', Validators.required],
            comment: ['', Validators.maxLength(250)],
            incapacity_type: [''],
            imss_certificate_number: [''],
            imss_percentage: [null],
            employer_percentage: [null],
            start_date: [''],
            end_date: [''],
        });
    }

    get f() {
        return this.form.controls;
    }

    get isIncapacidad(): boolean {
        return this.form?.get('incident_type')?.value === 'disability';
    }

    get isFaltaInjustificada(): boolean {
        return this.form?.get('incident_type')?.value === 'unexcused_absence';
    }

    get commentLength(): number {
        return (this.form?.get('comment')?.value || '').length;
    }

    get computedDays(): number | null {
        const start = this.form?.get('start_date')?.value;
        if (this.isFaltaInjustificada) {
            return start ? 1 : null;
        }
        const end = this.form?.get('end_date')?.value;
        if (!start || !end) return null;
        const d1 = new Date(start);
        const d2 = new Date(end);
        const diff = Math.round((d2.getTime() - d1.getTime()) / 86400000) + 1;
        return diff > 0 ? diff : 1;
    }

    get monthLabel(): string {
        const name = MONTH_NAMES[this.viewMonth];
        return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${this.viewYear}`;
    }

    get selectedDateLabel(): string {
        if (!this.selectedDate) return '--/--/----';
        const wd = WEEKDAY_NAMES_LONG[this.selectedDate.getDay()];
        const month = MONTH_NAMES[this.selectedDate.getMonth()];
        return `${wd.charAt(0).toUpperCase()}${wd.slice(1)}, ${this.selectedDate.getDate()} de ${month} de ${this.selectedDate.getFullYear()}`;
    }

    get monthRows(): any[] {
        return this.rows.filter((r) => {
            const d = new Date(this.rowIso(r));
            return d.getFullYear() === this.viewYear && d.getMonth() === this.viewMonth;
        });
    }

    get faltasDelMes(): number {
        return this.monthRows.filter((r) => r.incident_type === 'unexcused_absence').reduce((s, r) => s + (r.days || 1), 0);
    }

    get incapacidadesCount(): number {
        return this.monthRows.filter((r) => r.incident_type === 'disability').length;
    }

    get vacacionesDias(): number {
        return this.monthRows.filter((r) => r.incident_type === 'vacation').reduce((s, r) => s + (r.days || 1), 0);
    }

    get permisosDias(): number {
        return this.monthRows.filter((r) => r.incident_type === 'leave').reduce((s, r) => s + (r.days || 1), 0);
    }

    get visibleRows(): any[] {
        return this.showAllHistory ? this.rows : this.rows.slice(0, 3);
    }

    loadEmployee(): void {
        this.employeeService.getById(this.employeeId).subscribe({
            next: (resp: any) => { this.employee = resp; },
            error: () => { this.employee = null; }
        });
    }

    fetchIncidents(): void {
        this.loading = true;
        this.loadError = false;
        this.incidentService.list(this.employeeId).subscribe({
            next: (resp: any) => {
                this.rows = Array.isArray(resp) ? resp : (Array.isArray(resp?.data) ? resp.data : []);
                this.loading = false;
                this.buildCalendar();
            },
            error: () => {
                this.rows = [];
                this.loading = false;
                this.loadError = true;
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo obtener el historial de incidencias', icon: 'error' });
            }
        });
    }

    toggleHistory(): void {
        this.showAllHistory = !this.showAllHistory;
    }

    // ---- Formulario / drawer ----
    selectType(value: string): void {
        this.form.get('incident_type')?.setValue(value);
    }

    resetForm(): void {
        this.submitted = false;
        this.selectedIncident = null;
        this.rangeStart = null;
        this.rangeEnd = null;
        this.rangeSelectionInProgress = false;
        this.form.reset({
            incident_type: '',
            comment: '',
            incapacity_type: '',
            imss_certificate_number: '',
            imss_percentage: null,
            employer_percentage: null,
            start_date: '',
            end_date: '',
        });
    }

    save(): void {
        this.submitted = true;
        if (this.form.invalid || !this.employeeId || !this.selectedDate) {
            this.form.markAllAsTouched();
            if (!this.selectedDate) {
                this.utils.alertSwal({ title: 'Advertencia', text: 'Selecciona un día en el calendario para registrar la incidencia.', icon: 'warning' });
            }
            return;
        }

        const raw = this.form.getRawValue();
        const payload: any = {
            date: this.toIso(this.selectedDate),
            incident_type: raw.incident_type,
            days: this.computedDays || 1,
            comment: raw.comment || null,
            start_date: raw.start_date || null,
            end_date: this.isFaltaInjustificada ? (raw.start_date || null) : (raw.end_date || null),
        };

        if (raw.incident_type === 'disability') {
            payload.incapacity_type = raw.incapacity_type || null;
            payload.imss_certificate_number = raw.imss_certificate_number || null;
            payload.imss_percentage = raw.imss_percentage !== '' && raw.imss_percentage !== null ? Number(raw.imss_percentage) : null;
            payload.employer_percentage = raw.employer_percentage !== '' && raw.employer_percentage !== null ? Number(raw.employer_percentage) : null;
        }

        const isEdit = !!this.selectedIncident;

        this.spinner.show();
        const request$ = isEdit
            ? this.incidentService.update(this.employeeId, this.selectedIncident.id, payload)
            : this.incidentService.add(this.employeeId, payload);

        request$.subscribe({
            next: () => {
                this.spinner.hide();
                this.utils.alertSwal({
                    title: 'Operación exitosa',
                    text: `La incidencia se ${isEdit ? 'actualizó' : 'registró'} correctamente`,
                    icon: 'success'
                });
                this.resetForm();
                this.fetchIncidents();
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo guardar la incidencia', icon: 'error' });
            }
        });
    }

    deleteIncident(): void {
        if (!this.selectedIncident || !this.employeeId) return;

        this.utils.alertSwalConfir({
            title: '¿Eliminar incidencia?',
            text: 'Esta acción no se puede deshacer.',
            icon: 'warning',
            btn_confirm: 'Eliminar'
        }).then((result: any) => {
            if (!result?.isConfirmed) return;

            this.spinner.show();
            this.incidentService.delete(this.employeeId, this.selectedIncident.id).subscribe({
                next: () => {
                    this.spinner.hide();
                    this.utils.alertSwal({ title: 'Operación exitosa', text: 'La incidencia se eliminó correctamente', icon: 'success' });
                    this.resetForm();
                    this.fetchIncidents();
                },
                error: () => {
                    this.spinner.hide();
                    this.utils.alertSwal({ title: 'Error', text: 'No se pudo eliminar la incidencia', icon: 'error' });
                }
            });
        });
    }

    // ---- Calendario ----
    prevMonth(): void {
        this.viewMonth -= 1;
        if (this.viewMonth < 0) {
            this.viewMonth = 11;
            this.viewYear -= 1;
        }
        this.buildCalendar();
    }

    nextMonth(): void {
        this.viewMonth += 1;
        if (this.viewMonth > 11) {
            this.viewMonth = 0;
            this.viewYear += 1;
        }
        this.buildCalendar();
    }

    goToToday(): void {
        const now = new Date();
        this.viewYear = now.getFullYear();
        this.viewMonth = now.getMonth();
        this.buildCalendar();
    }

    selectDay(cell: CalendarDay | null): void {
        if (!cell) return;

        // Falta injustificada usa una sola fecha: cada click reinicia la
        // selección a ese único día (no hay segundo click de "fin de rango").
        if (this.isFaltaInjustificada) {
            this.rangeStart = cell.date;
            this.rangeEnd = cell.date;
            this.rangeSelectionInProgress = false;
            this.selectedDate = cell.date;

            const match = this.rows.find((r) => this.rowIso(r) === cell.iso);
            if (match) {
                this.patchFormFromIncident(match, cell.iso);
            } else {
                this.selectedIncident = null;
                this.submitted = false;
                this.form.patchValue({ start_date: cell.iso, end_date: cell.iso });
            }
            return;
        }

        if (!this.rangeSelectionInProgress) {
            this.beginRangeSelection(cell);
        } else {
            this.completeRangeSelection(cell);
        }
    }

    /** Primer click de un rango: carga la incidencia existente en ese día, o inicia una nueva selección (sin perder el tipo ya elegido). */
    private beginRangeSelection(cell: CalendarDay): void {
        this.selectedDate = cell.date;

        const match = this.rows.find((r) => this.rowIso(r) === cell.iso);
        if (match) {
            this.patchFormFromIncident(match, cell.iso);
            this.rangeStart = cell.date;
            this.rangeEnd = match.end_date ? this.parseIso(this.rowIso({ date: match.end_date })) : cell.date;
            this.rangeSelectionInProgress = false;
            return;
        }

        this.selectedIncident = null;
        this.submitted = false;
        this.rangeStart = cell.date;
        this.rangeEnd = cell.date;
        this.rangeSelectionInProgress = true;
        this.form.patchValue({
            comment: '',
            incapacity_type: '',
            imss_certificate_number: '',
            imss_percentage: null,
            employer_percentage: null,
            start_date: cell.iso,
            end_date: cell.iso,
        });
    }

    /** Segundo click de un rango: cierra el rango, pintando automáticamente Fecha de inicio/Fecha de fin. */
    private completeRangeSelection(cell: CalendarDay): void {
        if (cell.date.getTime() < this.rangeStart!.getTime()) {
            this.rangeEnd = this.rangeStart;
            this.rangeStart = cell.date;
        } else {
            this.rangeEnd = cell.date;
        }
        this.selectedDate = this.rangeStart;
        this.rangeSelectionInProgress = false;
        this.form.patchValue({
            start_date: this.toIso(this.rangeStart!),
            end_date: this.toIso(this.rangeEnd!),
        });
    }

    private patchFormFromIncident(match: any, iso: string): void {
        this.selectedIncident = match;
        this.submitted = false;
        this.form.patchValue({
            incident_type: match.incident_type,
            comment: match.comment || '',
            incapacity_type: match.incapacity_type || '',
            imss_certificate_number: match.imss_certificate_number || '',
            imss_percentage: match.imss_percentage ?? null,
            employer_percentage: match.employer_percentage ?? null,
            start_date: match.start_date ? this.rowIso({ date: match.start_date }) : iso,
            end_date: match.end_date ? this.rowIso({ date: match.end_date }) : iso,
        });
    }

    isSelected(cell: CalendarDay | null): boolean {
        if (!cell) return false;
        return this.isSameDate(cell.date, this.rangeStart) || this.isSameDate(cell.date, this.rangeEnd);
    }

    isInRange(cell: CalendarDay | null): boolean {
        if (!cell || !this.rangeStart || !this.rangeEnd) return false;
        const t = cell.date.getTime();
        return t > this.rangeStart.getTime() && t < this.rangeEnd.getTime();
    }

    private isSameDate(a: Date | null, b: Date | null): boolean {
        if (!a || !b) return false;
        return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    }

    private parseIso(iso: string): Date {
        const [y, m, d] = iso.split('-').map(Number);
        return new Date(y, m - 1, d);
    }

    isWeekend(cell: CalendarDay | null): 'saturday' | 'sunday' | '' {
        if (!cell) return '';
        const day = cell.date.getDay();
        if (day === 6) return 'saturday';
        if (day === 0) return 'sunday';
        return '';
    }

    private buildCalendar(): void {
        const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
        const firstOfMonth = new Date(this.viewYear, this.viewMonth, 1);
        const firstWeekday = (firstOfMonth.getDay() + 6) % 7;

        const cells: (CalendarDay | null)[] = [];
        for (let i = 0; i < firstWeekday; i++) {
            cells.push(null);
        }
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(this.viewYear, this.viewMonth, day);
            const iso = this.toIso(date);
            cells.push({ date, iso, day, dots: this.dotsForDate(iso) });
        }
        while (cells.length % 7 !== 0) {
            cells.push(null);
        }

        const weeks: (CalendarDay | null)[][] = [];
        for (let i = 0; i < cells.length; i += 7) {
            weeks.push(cells.slice(i, i + 7));
        }
        this.calendarWeeks = weeks;
    }

    private dotsForDate(iso: string): string[] {
        return this.rows.filter((r) => this.rowIso(r) === iso).map((r) => this.incidentColor(r.incident_type));
    }

    private rowIso(row: any): string {
        return String(row.date).split('T')[0].split(' ')[0];
    }

    private toIso(date: Date): string {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    }

    goBack(): void {
        // "Registrar incidencia" siempre se abre desde Listado de colaboradores
        // (ver EmployeeActionsComponent.registerIncident), así que la pantalla
        // anterior real es esa lista, no el detalle del colaborador.
        this.location.back();
    }

    // ---- Modal dar de baja ----
    openTerminationModal(): void {
        if (!this.employee?.id) return;
        if (this.employee.status === 'terminated') {
            this.utils.alertSwal({ title: 'Colaborador dado de baja', text: 'Este colaborador ya se encuentra dado de baja.', icon: 'info' });
            return;
        }
        this.terminationModal.open({ id: this.employee.id, fullName: this.getEmployeeName() });
    }

    onEmployeeTerminated(event: TerminationEvent): void {
        this.loadEmployee();
    }

    incidentMeta(value: string): IncidentTypeMeta | undefined {
        return this.typeButtons.find((t) => t.value === value) || (value === 'other' ? OTHER_TYPE_META : undefined);
    }

    incidentLabel(value: string): string {
        return this.incidentMeta(value)?.label || value || '--';
    }

    incidentColor(value: string): string {
        return this.incidentMeta(value)?.color || '#6b7280';
    }

    getEmployeeName(): string {
        if (!this.employee) return '';
        const parts = [this.employee.first_name, this.employee.last_name, this.employee.mother_last_name].filter(Boolean);
        return parts.join(' ') || '--';
    }

    get photoUrl(): string | null {
        return this.employeeService.getPhotoUrl(this.employee?.photo);
    }

    get initials(): string {
        if (!this.employee) return '--';
        const a = String(this.employee.first_name || '').charAt(0);
        const b = String(this.employee.last_name || '').charAt(0);
        const value = `${a}${b}`.toUpperCase();
        return value || '--';
    }

    get statusIsActive(): boolean {
        return this.employee?.status !== 'terminated';
    }

    get statusLabel(): string {
        return this.statusIsActive ? 'Alta' : 'Baja';
    }

    formatDate(value: any): string {
        if (!value) return '--/--/----';
        const datePart = String(value).split('T')[0].split(' ')[0];
        const [yyyy, mm, dd] = datePart.split('-');
        if (!yyyy || !mm || !dd) return '--/--/----';
        return `${dd}/${mm}/${yyyy}`;
    }
}
